/* ============================================================================
   DESCARGA PREVIA DE MAPAS

   El service worker cachea los mosaicos que ya se visitaron. Volando en la
   sierra sin cobertura, el mapa queda en blanco donde nunca se navegó — y un
   piloto que abre la aplicación en vuelo y ve un mapa vacío no la vuelve a
   abrir.

   ══════════════════════════════════════════════════════════════════════════
   EL NÚMERO QUE ORDENA TODO ESTE MÓDULO

   Sudamérica entera (12°N a 56°S, 82°O a 34°O), medido:

     zoom  8 →      2.030 mosaicos ≈    36 MB
     zoom 10 →     31.602 mosaicos ≈   556 MB
     zoom 12 →    498.317 mosaicos ≈  8,7 GB
     zoom 13 →  1.991.446 mosaicos ≈   35 GB

   **No se puede descargar un continente al detalle.** Una descarga de medio
   millón de peticiones tumba el servidor de mosaicos, llena el dispositivo y
   no termina nunca.

   Por eso este módulo hace tres cosas antes de descargar nada:

     1. CUENTA los mosaicos y estima el espacio.
     2. RECHAZA lo que no es viable, con el motivo y una alternativa concreta.
     3. Descarga con freno, para no tumbar el servidor ni agotar la batería.

   Un botón que arranca una descarga imposible y falla a la mitad es peor que
   uno que dice «esto son 8,7 GB, elige menos zoom o menos área».
   ══════════════════════════════════════════════════════════════════════════
   ========================================================================== */

/* Los números que lee el piloto se escriben con la convención del proyecto
   —coma decimal, punto de miles—, no con la del equipo donde corre. */
import { numero } from "./formato.js";

/** Bytes que ocupa un mosaico, medido sobre los que ya están en caché. */
export const BYTES_POR_MOSAICO = 18 * 1024;

/**
 * Topes de descarga.
 *
 * No son gustos: `maximoMosaicos` es lo que un dispositivo aguanta sin que la
 * descarga tarde horas, y `avisarDesde` es donde conviene que el piloto sepa
 * en qué se está metiendo antes de empezar.
 */
export const TOPES = {
  avisarDesde: 5000,
  maximoMosaicos: 60000,
  /* Peticiones simultáneas. Más alto satura la red del dispositivo y hace que
     los servidores de mosaicos empiecen a rechazar. */
  enParalelo: 6,
};

/** Zoom mínimo y máximo que tiene sentido descargar. */
export const ZOOM = { minimo: 5, maximo: 14 };

const num = (v) => (Number.isFinite(v) ? v : null);

/** Columna de un mosaico para una longitud. */
export function columnaDe(lon, z) {
  return Math.floor(((lon + 180) / 360) * 2 ** z);
}

/** Fila de un mosaico para una latitud (proyección Mercator). */
export function filaDe(lat, z) {
  const r = (lat * Math.PI) / 180;
  return Math.floor(((1 - Math.log(Math.tan(r) + 1 / Math.cos(r)) / Math.PI) / 2) * 2 ** z);
}

/**
 * Cuenta los mosaicos de una región entre dos zooms.
 *
 * Devuelve también el desglose por zoom: sirve para que el piloto vea de dónde
 * sale el número y pueda bajar un nivel en vez de abandonar.
 */
export function contarMosaicos(region, zoomMin, zoomMax) {
  const norte = num(region?.norte);
  const sur = num(region?.sur);
  const oeste = num(region?.oeste);
  const este = num(region?.este);

  if ([norte, sur, oeste, este].some((v) => v === null)) {
    return { total: 0, porZoom: [], valido: false, motivo: "La región no tiene límites válidos." };
  }
  if (sur >= norte || oeste >= este) {
    return { total: 0, porZoom: [], valido: false, motivo: "Los límites de la región están invertidos." };
  }

  const zi = Math.max(ZOOM.minimo, Math.round(num(zoomMin) ?? ZOOM.minimo));
  const zf = Math.min(ZOOM.maximo, Math.round(num(zoomMax) ?? ZOOM.minimo));
  if (zf < zi) {
    return { total: 0, porZoom: [], valido: false, motivo: "El zoom máximo es menor que el mínimo." };
  }

  const porZoom = [];
  let total = 0;

  for (let z = zi; z <= zf; z += 1) {
    /* Se acota a los límites de la proyección: pedir más allá de 85° daría
       filas que no existen y peticiones que siempre fallan. */
    const latN = Math.min(norte, 85.05);
    const latS = Math.max(sur, -85.05);

    const x1 = columnaDe(oeste, z);
    const x2 = columnaDe(este, z);
    const y1 = filaDe(latN, z);
    const y2 = filaDe(latS, z);

    const cantidad = (x2 - x1 + 1) * (y2 - y1 + 1);
    porZoom.push({ zoom: z, mosaicos: cantidad, x1, x2, y1, y2 });
    total += cantidad;
  }

  return {
    total,
    porZoom,
    valido: true,
    bytes: total * BYTES_POR_MOSAICO,
    mb: Math.round((total * BYTES_POR_MOSAICO) / 1024 / 1024),
  };
}

/**
 * ¿Se puede descargar esto?
 *
 * Cuando no, **dice qué hacer**: un «demasiado grande» sin alternativa deja al
 * piloto sin salida, y la salida siempre existe —menos zoom o menos área—.
 */
export function evaluarDescarga(region, zoomMin, zoomMax, topes = TOPES) {
  const t = { ...TOPES, ...(topes || {}) };
  const cuenta = contarMosaicos(region, zoomMin, zoomMax);

  if (!cuenta.valido) {
    return { ...cuenta, permitida: false, avisos: [] };
  }

  const avisos = [];

  if (cuenta.total > t.maximoMosaicos) {
    /* La alternativa se calcula, no se sugiere en abstracto: se busca hasta
       qué zoom entra la misma región. */
    let zoomQueEntra = null;
    for (let z = Math.round(num(zoomMax) ?? ZOOM.minimo); z >= (num(zoomMin) ?? ZOOM.minimo); z -= 1) {
      if (contarMosaicos(region, zoomMin, z).total <= t.maximoMosaicos) { zoomQueEntra = z; break; }
    }

    return {
      ...cuenta,
      permitida: false,
      motivo: `${numero(cuenta.total)} mosaicos (${cuenta.mb} MB) superan el máximo `
        + `de ${numero(t.maximoMosaicos)}. `
        + (zoomQueEntra
          ? `Con zoom hasta ${zoomQueEntra} entran ${numero(contarMosaicos(region, zoomMin, zoomQueEntra).total)}.`
          : "Achica la región: ni el zoom más bajo alcanza."),
      avisos,
    };
  }

  if (cuenta.total > t.avisarDesde) {
    avisos.push(`${numero(cuenta.total)} mosaicos, unos ${cuenta.mb} MB. `
      + "Conviene descargar con wifi y el dispositivo enchufado.");
  }

  return { ...cuenta, permitida: true, motivo: null, avisos };
}

/**
 * Las direcciones de los mosaicos a descargar.
 *
 * Se generan **por zoom, de menor a mayor**: si la descarga se corta a la
 * mitad, lo que quedó es el mapa completo a menos detalle, no medio mapa al
 * detalle. Un mapa entero y grueso sirve para volar; medio mapa fino, no.
 */
export function* direccionesDeMosaicos(region, zoomMin, zoomMax, plantilla) {
  const cuenta = contarMosaicos(region, zoomMin, zoomMax);
  if (!cuenta.valido) return;

  for (const { zoom, x1, x2, y1, y2 } of cuenta.porZoom) {
    for (let y = y1; y <= y2; y += 1) {
      for (let x = x1; x <= x2; x += 1) {
        yield plantilla
          .replace("{z}", String(zoom))
          .replace("{x}", String(x))
          .replace("{y}", String(y))
          /* Los servidores con subdominios: se reparte para no golpear uno
             solo, que es lo que dispara los rechazos. */
          .replace("{s}", "abc"[(x + y) % 3]);
      }
    }
  }
}

/**
 * Estado de una descarga en curso.
 *
 * `restanteSegundos` sale de la velocidad medida, no de una estimación fija:
 * una cifra inventada que no se cumple hace que el piloto cancele algo que
 * estaba por terminar.
 */
export function progresoDescarga(entrada) {
  /* Se desestructura DESPUÉS de comprobar: `{...} = null` revienta, y esto lo
     llama la pantalla en cada ciclo con lo que haya. */
  const {
    hechos = 0, total = 0, fallidos = 0, desdeMs = null, ahoraMs = Date.now(),
  } = (entrada && typeof entrada === "object") ? entrada : {};

  const t = Math.max(0, Math.round(num(total) ?? 0));
  const h = Math.min(t, Math.max(0, Math.round(num(hechos) ?? 0)));

  const transcurrido = num(desdeMs) !== null ? Math.max(1, ahoraMs - desdeMs) : null;
  const porSegundo = transcurrido && h > 0 ? (h / transcurrido) * 1000 : null;

  return {
    hechos: h,
    total: t,
    fallidos: Math.max(0, Math.round(num(fallidos) ?? 0)),
    fraccion: t > 0 ? h / t : 0,
    porcentaje: t > 0 ? Math.round((h / t) * 100) : 0,
    porSegundo: porSegundo ? Math.round(porSegundo * 10) / 10 : null,
    restanteSegundos: porSegundo && porSegundo > 0 ? Math.round((t - h) / porSegundo) : null,
    /* Los fallos no detienen la descarga —un mosaico que no está no arruina el
       mapa— pero muchos seguidos sí: significa que se perdió la red o que el
       servidor está rechazando. */
    demasiadosFallos: h > 20 && fallidos / Math.max(1, h + fallidos) > 0.5,
  };
}

/** Regiones frecuentes, para no tener que dibujarlas cada vez. */
export const REGIONES = [
  { clave: "peru", nombre: "Perú", norte: 0.5, sur: -18.5, oeste: -81.5, este: -68.5 },
  { clave: "ecuador", nombre: "Ecuador", norte: 1.7, sur: -5.1, oeste: -81.2, este: -75 },
  { clave: "chile-norte", nombre: "Chile norte", norte: -17, sur: -32, oeste: -71, este: -67 },
  { clave: "sudamerica", nombre: "Sudamérica", norte: 12, sur: -56, oeste: -82, este: -34 },
];
